import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Wand2, Loader2, Film, Clock, AlertCircle, ImagePlus, X as XIcon, ShieldAlert, ChevronDown, Crown, Mic, Trash2, Volume2, UserPlus, Plus, Store, Share2, Coins, TrendingUp, Repeat2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SAMPLE_PROMPTS = [
  "A neon-soaked Tokyo alley at midnight, rain reflecting purple signs, slow tracking shot",
  "An astronaut break-dancing on Mars while pink clouds drift across a black sky",
  "Macro shot of a glowing jellyfish drifting through a dark cyber-ocean, bioluminescent",
  "A cat DJ wearing oversized headphones spinning records at a rooftop party at sunset",
];

const IMAGE_SAMPLE_PROMPTS = [
  "Slow zoom-in with dramatic neon lighting sweeping across the frame",
  "The subject slowly turns their head, hair moving in a gentle breeze",
  "Camera pans right while volumetric light and dust particles drift through the scene",
  "Cinematic parallax with the foreground moving slowly and clouds drifting overhead",
];

const DURATIONS = [
  { value: 4, label: "4s", note: "fastest" },
  { value: 8, label: "8s", note: "medium" },
  { value: 12, label: "12s", note: "longest" },
];

const STATUS_LABELS = {
  pending: { label: "queued", color: "#A0A0AB" },
  generating: { label: "rendering frames", color: "#00FFFF" },
  uploading: { label: "uploading to feed", color: "#39FF14" },
  done: { label: "ready", color: "#39FF14" },
  failed: { label: "failed", color: "#FF3B30" },
};

const ACTIVE_STATUSES = ["pending", "generating", "uploading"];

export default function GenerateVideo() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState(4);
  const [referenceImage, setReferenceImage] = useState(null); // File
  const [referencePreview, setReferencePreview] = useState(null); // data URL
  const [safetyOpen, setSafetyOpen] = useState(false);
  const imageInputRef = useRef(null);
  const [caption, setCaption] = useState("");
  // Provider selection: "sora" (default; text-to-video OR image-to-video)
  // or "kling" (Kling 3.0 Standard via fal.ai — image-to-video only, with
  // native audio). Kling auto-selects the moment a reference image is
  // uploaded because it renders sharper motion on human portraits than
  // Sora 2 on this tier. Users can toggle back to Sora manually.
  const [provider, setProvider] = useState("sora");
  const [providerTouched, setProviderTouched] = useState(false);
  const [job, setJob] = useState(null);
  const [recent, setRecent] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);
  const [usage, setUsage] = useState(null);

  // Kling Voice Library — voice_ids that lock a character voice across
  // generations. Users clone a 5-30s audio sample (once), then reference
  // the returned voice IDs in the prompt as <<<voice_1>>> / <<<voice_2>>>.
  // Max 2 voices per generation (Kling 3.0 API constraint).
  const [voices, setVoices] = useState([]);
  const [selectedVoiceIds, setSelectedVoiceIds] = useState([]);
  const [voiceUploadOpen, setVoiceUploadOpen] = useState(false);
  const [voiceLabel, setVoiceLabel] = useState("");
  const [voiceFile, setVoiceFile] = useState(null);
  const [voiceUploading, setVoiceUploading] = useState(false);
  const voiceFileRef = useRef(null);

  // Character Collections — a Character bundles a reference photo + an
  // optional linked voice_id under a human name. Selecting a Character
  // auto-loads its image and voice into the generation form.
  const [characters, setCharacters] = useState([]);
  const [selectedCharacterId, setSelectedCharacterId] = useState(null);
  const [characterModalOpen, setCharacterModalOpen] = useState(false);
  const [charName, setCharName] = useState("");
  const [charDescription, setCharDescription] = useState("");
  const [charImageFile, setCharImageFile] = useState(null);
  const [charImagePreview, setCharImagePreview] = useState(null);
  const [charVoiceId, setCharVoiceId] = useState("");
  const [charSaving, setCharSaving] = useState(false);
  const charImageRef = useRef(null);

  // Marketplace state — browsing/remixing/tipping other creators'
  // published characters.
  const [marketOpen, setMarketOpen] = useState(false);
  const [marketItems, setMarketItems] = useState([]);
  const [marketLoading, setMarketLoading] = useState(false);
  const [marketSort, setMarketSort] = useState("trending");
  const [remixingId, setRemixingId] = useState(null);
  const [tipTargetId, setTipTargetId] = useState(null);
  const [tipAmount, setTipAmount] = useState("100"); // cents, string for input control
  const [tipSubmitting, setTipSubmitting] = useState(false);

  // Smart default: whenever the user picks/clears an image, auto-flip the
  // provider — but only if they haven't manually overridden it (respects
  // explicit user choice per session).
  useEffect(() => {
    if (providerTouched) return;
    setProvider(referenceImage ? "kling" : "sora");
  }, [referenceImage, providerTouched]);

  const loadUsage = async () => {
    try {
      const r = await api.get("/sora/usage");
      setUsage(r.data);
    } catch { /* ignore */ }
  };

  const loadVoices = async () => {
    try {
      const r = await api.get("/ai/voices");
      setVoices(r.data?.voices || []);
    } catch { /* ignore */ }
  };

  // Auto-poll while any voice is still cloning ("pending"). Stops once
  // every row is either "ready" or "failed" so we don't hammer the API.
  useEffect(() => {
    const hasPending = voices.some((v) => v.status === "pending");
    if (!hasPending) return undefined;
    const id = setInterval(loadVoices, 4000);
    return () => clearInterval(id);
  }, [voices]);

  const toggleVoice = (voice_id) => {
    setSelectedVoiceIds((prev) => {
      if (prev.includes(voice_id)) return prev.filter((v) => v !== voice_id);
      if (prev.length >= 2) {
        toast.error("Max 2 voices per generation");
        return prev;
      }
      return [...prev, voice_id];
    });
  };

  const uploadVoice = async () => {
    if (!voiceFile) {
      toast.error("Pick an audio sample first");
      return;
    }
    if (!voiceLabel.trim()) {
      toast.error("Name this voice");
      return;
    }
    setVoiceUploading(true);
    try {
      const fd = new FormData();
      fd.append("label", voiceLabel.trim());
      fd.append("audio", voiceFile);
      // Server returns 202 immediately + a pending row; polling picks up
      // the flip to "ready" or "failed" so this toast is the "we
      // received your sample" ack, not a "cloning complete" one.
      const r = await api.post("/ai/voices", fd);
      toast.success(`Cloning "${r.data.label}" — this usually takes 30-90 seconds`);
      setVoiceUploadOpen(false);
      setVoiceLabel("");
      setVoiceFile(null);
      if (voiceFileRef.current) voiceFileRef.current.value = "";
      loadVoices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice clone failed");
    } finally {
      setVoiceUploading(false);
    }
  };

  const deleteVoice = async (voiceOrPendingId) => {
    if (!window.confirm("Delete this voice? Ongoing generations still work.")) return;
    try {
      await api.delete(`/ai/voices/${voiceOrPendingId}`);
      setSelectedVoiceIds((prev) => prev.filter((v) => v !== voiceOrPendingId));
      loadVoices();
      loadCharacters(); // characters that referenced this voice get their voice_id cleared
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const loadCharacters = async () => {
    try {
      const r = await api.get("/ai/characters");
      setCharacters(r.data?.characters || []);
    } catch { /* ignore */ }
  };

  const selectCharacter = (char) => {
    if (selectedCharacterId === char.character_id) {
      // Deselect + clear any hydrated selection
      setSelectedCharacterId(null);
      setSelectedVoiceIds([]);
      return;
    }
    setSelectedCharacterId(char.character_id);
    // Force Kling provider (backend enforces this too)
    setProvider("kling");
    setProviderTouched(true);
    // Preselect the character's voice if it has one
    if (char.voice_id) {
      setSelectedVoiceIds([char.voice_id]);
    } else {
      setSelectedVoiceIds([]);
    }
    // Clear any uploaded reference image (character's photo will be used)
    clearReferenceImage();
    toast.success(`Character "${char.name}" loaded`);
  };

  const openCharacterModal = () => {
    setCharName("");
    setCharDescription("");
    setCharImageFile(null);
    setCharImagePreview(null);
    setCharVoiceId("");
    setCharacterModalOpen(true);
  };

  const pickCharacterImage = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8 MB)");
      return;
    }
    setCharImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setCharImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const saveCharacter = async () => {
    if (!charName.trim()) {
      toast.error("Name your character");
      return;
    }
    if (!charImageFile) {
      toast.error("Upload a reference photo");
      return;
    }
    setCharSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", charName.trim());
      fd.append("description", charDescription.trim());
      if (charVoiceId) fd.append("voice_id", charVoiceId);
      fd.append("image", charImageFile);
      await api.post("/ai/characters", fd, {
        
      });
      toast.success(`Character "${charName.trim()}" saved`);
      setCharacterModalOpen(false);
      loadCharacters();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setCharSaving(false);
    }
  };

  const deleteCharacter = async (character_id) => {
    if (!window.confirm("Delete this character?")) return;
    try {
      await api.delete(`/ai/characters/${character_id}`);
      if (selectedCharacterId === character_id) {
        setSelectedCharacterId(null);
      }
      loadCharacters();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const togglePublish = async (char) => {
    try {
      const url = char.is_published
        ? `/ai/characters/${char.character_id}/unpublish`
        : `/ai/characters/${char.character_id}/publish`;
      await api.post(url);
      toast.success(char.is_published ? "Removed from Marketplace" : "Published to Marketplace");
      loadCharacters();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    }
  };

  const openMarketplace = async () => {
    setMarketOpen(true);
    await loadMarketplace(marketSort);
  };

  const loadMarketplace = async (sort) => {
    setMarketLoading(true);
    try {
      const r = await api.get(`/marketplace/characters?sort=${sort}&limit=48`);
      setMarketItems(r.data?.items || []);
    } catch {
      /* ignore */
    } finally {
      setMarketLoading(false);
    }
  };

  const changeMarketSort = (sort) => {
    setMarketSort(sort);
    loadMarketplace(sort);
  };

  const remixCharacter = async (mp) => {
    setRemixingId(mp.character_id);
    try {
      const r = await api.post(`/marketplace/characters/${mp.character_id}/remix`);
      toast.success(`"${r.data.name}" remixed → your library`);
      loadCharacters();
      // Refresh marketplace so remix_count updates locally
      loadMarketplace(marketSort);
      // Auto-select the new character in the form
      setSelectedCharacterId(r.data.character_id);
      setProvider("kling");
      setProviderTouched(true);
      if (r.data.voice_id) {
        setSelectedVoiceIds([r.data.voice_id]);
        loadVoices();
      }
      clearReferenceImage();
      setMarketOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remix failed");
    } finally {
      setRemixingId(null);
    }
  };

  const submitTip = async () => {
    if (!tipTargetId) return;
    const cents = parseInt(tipAmount, 10);
    if (!Number.isFinite(cents) || cents < 25) {
      toast.error("Minimum tip is $0.25 (25 cents)");
      return;
    }
    setTipSubmitting(true);
    try {
      const r = await api.post(`/marketplace/characters/${tipTargetId}/tip`, {
        amount_cents: cents,
        message: null,
      });
      toast.success(`Tipped $${(r.data.amount_cents / 100).toFixed(2)} — creator got ${r.data.creator_coins}¢`);
      setTipTargetId(null);
      setTipAmount("100");
      loadMarketplace(marketSort);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tip failed");
    } finally {
      setTipSubmitting(false);
    }
  };

  const loadRecent = async () => {
    try {
      const r = await api.get("/ai/generate-video");
      setRecent(r.data);
      // resume polling if an active job exists
      const active = r.data.find((j) => ACTIVE_STATUSES.includes(j.status));
      if (active && !job) setJob(active);
    } catch {
      /* ignore */
    }
  };

  const cancelJob = async (jobId) => {
    if (!jobId) return;
    if (!window.confirm("Cancel this render? The Sora job will be marked as failed and you can retry.")) return;
    try {
      const r = await api.post(`/ai/generate-video/${jobId}/cancel`);
      setJob(r.data);
      clearInterval(pollRef.current);
      toast.success("Render cancelled");
      loadRecent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  useEffect(() => {
    if (user) {
      loadRecent();
      loadUsage();
      loadVoices();
      loadCharacters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!job || !ACTIVE_STATUSES.includes(job.status)) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/ai/generate-video/${job.job_id}`);
        setJob(r.data);
        if (!ACTIVE_STATUSES.includes(r.data.status)) {
          clearInterval(pollRef.current);
          loadRecent();
          if (r.data.status === "done") {
            toast.success("Video ready — opening the feed");
          } else if (r.data.status === "failed") {
            toast.error(r.data.error || "Generation failed");
          }
        }
      } catch {
        /* ignore */
      }
    }, 4000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.job_id, job?.status]);

  if (!user) {
    return (
      <div className="md:ml-60 min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF007F] mb-3">
            // auth.required
          </p>
          <h2 className="font-display font-black text-3xl mb-4">
            sign in to generate
          </h2>
          <button
            data-testid="gen-go-login-btn"
            onClick={() => navigate("/login")}
            className="bg-[#39FF14] text-black font-bold uppercase tracking-wider px-6 py-3"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e?.preventDefault();
    if (!prompt.trim()) {
      toast.error("Describe the video first");
      return;
    }
    if (provider === "kling" && !referenceImage && !selectedCharacterId) {
      toast.error("Kling 3.0 needs a reference image or Character — upload a photo, pick a Character, or switch to Sora");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("prompt", prompt);
      fd.append("duration", String(duration));
      fd.append("caption", caption || "");
      fd.append("size", "720x1280");
      fd.append("provider", provider);
      if (provider === "kling" && selectedCharacterId) {
        fd.append("character_id", selectedCharacterId);
      }
      if (provider === "kling" && selectedVoiceIds.length) {
        fd.append("voice_ids", selectedVoiceIds.join(","));
      }
      if (referenceImage && !selectedCharacterId) {
        // Character's image trumps any uploaded image; only send upload
        // when no character is selected.
        fd.append("image", referenceImage);
      }
      const r = await api.post("/ai/generate-video", fd, {
        
      });
      setJob(r.data);
      toast.success(
        provider === "kling"
          ? "Kling 3.0 is animating your image with audio — 60-180 sec"
          : referenceImage
            ? "Sora is animating your image — 2–5 min"
            : "Sora generation started — 2–5 min"
      );
      loadRecent();
      loadUsage();
      // Clear the image on submit — next generation starts fresh
      clearReferenceImage();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to start generation";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleImagePick = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8 MB)");
      return;
    }
    setReferenceImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setReferencePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearReferenceImage = () => {
    setReferenceImage(null);
    setReferencePreview(null);
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const status = job ? STATUS_LABELS[job.status] || STATUS_LABELS.pending : null;
  const isActive = job && ACTIVE_STATUSES.includes(job.status);

  return (
    <div className="md:ml-60 min-h-screen pb-24 md:pb-12">
      <div className="max-w-3xl mx-auto pt-10 px-6">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00FFFF] mb-2">
          // sora.engine
        </p>
        <h1 className="font-display font-black text-4xl mb-2 flex items-center gap-3">
          Generate with AI
          <Wand2 className="h-7 w-7 text-[#FF007F]" />
        </h1>
        <p className="text-white/50 text-sm mb-10">
          Describe a moment. Sora 2 renders it as a vertical short and drops it
          straight into the feed.
        </p>

        {/* Sora monthly quota meter — Pro-aware */}
        {usage && (
          <div
            data-testid="sora-usage-meter"
            className={`mb-8 border p-4 flex items-center justify-between gap-4 ${
              usage.remaining <= 0
                ? "border-[#FF3B30]/50 bg-[#FF3B30]/5"
                : usage.is_pro
                ? "border-[#B026FF]/40 bg-[#B026FF]/5"
                : "border-white/10 bg-[#0A0A0C]"
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              {usage.is_pro && <Crown className="h-4 w-4 text-[#B026FF]" strokeWidth={2.2} />}
              <div className="min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1">
                  // {usage.is_pro ? "pro.tier" : "free.tier"} · ai.generations · sora + kling
                </p>
                <p className="font-display font-black text-white text-xl">
                  <span data-testid="sora-usage-remaining">{usage.remaining}</span>
                  <span className="text-white/40 font-mono text-sm ml-2">/ {usage.limit} left this month</span>
                </p>
              </div>
            </div>
            {!usage.is_pro && (
              <button
                type="button"
                data-testid="sora-pro-upsell-btn"
                onClick={() => navigate("/pro")}
                className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-[#B026FF] text-white font-bold uppercase tracking-widest text-xs hover:shadow-[0_0_20px_rgba(176,38,255,0.55)] transition-all"
              >
                <Crown className="h-3.5 w-3.5" />
                pro · 30/mo
              </button>
            )}
          </div>
        )}

        {/* Active job banner */}
        {job && (
          <div
            data-testid="job-status-banner"
            className="bg-[#0A0A0C] border border-white/10 p-5 mb-8 relative overflow-hidden"
          >
            <div
              className="absolute left-0 top-0 bottom-0 w-1"
              style={{ background: status?.color }}
            />
            <div className="flex items-center justify-between gap-4 mb-2">
              <p
                className="font-mono text-xs uppercase tracking-[0.3em]"
                style={{ color: status?.color }}
              >
                // {status?.label}
              </p>
              {isActive && (
                <Loader2
                  className="h-4 w-4 animate-spin"
                  style={{ color: status?.color }}
                />
              )}
            </div>
            <p className="text-white text-sm mb-3 line-clamp-2">
              {job.prompt}
            </p>
            {job.status === "done" && job.video_id && (
              <button
                data-testid="watch-generated-btn"
                onClick={() => navigate(`/v/${job.video_id}`)}
                className="bg-[#39FF14] text-black font-bold uppercase tracking-widest text-xs px-5 py-2.5 hover:shadow-[0_0_20px_rgba(57,255,20,0.5)]"
              >
                Watch your video →
              </button>
            )}
            {job.status === "failed" && (
              <div className="mt-2">
                <div className="flex items-start gap-2 text-[#FF3B30] text-xs">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{job.error || "Unknown error"}</span>
                </div>
                {/^.*budget.*$/i.test(job.error || "") && (
                  <div
                    data-testid="budget-help"
                    className="mt-3 border border-[#00FFFF]/40 bg-[#00FFFF]/5 p-3 text-xs text-white/80 space-y-1"
                  >
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00FFFF]">
                      // proxy.cap
                    </p>
                    <p>
                      This is <span className="text-white font-semibold">not</span>{" "}
                      your wallet balance — the Emergent Sora proxy has a
                      separate per-key spend cap that's been reached for this
                      period. Contact{" "}
                      <span className="text-[#00FFFF] font-mono">
                        Emergent Support
                      </span>{" "}
                      to raise the cap, or wait for it to reset.
                    </p>
                  </div>
                )}
                {/insufficient|balance/i.test(job.error || "") &&
                  !/budget/i.test(job.error || "") && (
                    <div
                      data-testid="balance-help"
                      className="mt-3 border border-[#FF007F]/40 bg-[#FF007F]/5 p-3 text-xs text-white/80"
                    >
                      Sora video generation is metered. Open{" "}
                      <span className="font-mono text-[#FF007F]">
                        Profile → Universal Key → Add Balance
                      </span>{" "}
                      in the Emergent dashboard to top up, then retry.
                    </div>
                  )}
                {/safety|content_policy|moderation/i.test(job.error || "") && (
                  <div
                    data-testid="policy-help"
                    className="mt-3 border border-[#FF007F]/40 bg-[#FF007F]/5 p-3 text-xs text-white/80"
                  >
                    The prompt tripped Sora's safety filter. Avoid named real
                    people, brands, violent or explicit content — then try again
                    with neutral wording.
                  </div>
                )}
              </div>
            )}
            {isActive && (
              <div className="flex items-center justify-between gap-3 mt-2">
                <p className="font-mono text-[10px] text-white/40 uppercase tracking-widest">
                  ⏱ typically 2–5 minutes · you can leave this page
                </p>
                <button
                  type="button"
                  data-testid="cancel-job-btn"
                  onClick={() => cancelJob(job.job_id)}
                  className="font-mono text-[10px] uppercase tracking-widest border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:border-[#FF3B30] px-3 py-1.5 transition-colors flex items-center gap-1.5"
                  title="Cancel this render"
                >
                  <XIcon className="h-3 w-3" />
                  cancel
                </button>
              </div>
            )}
          </div>
        )}

        <form onSubmit={submit} className="space-y-8" data-testid="gen-form">
          {/* Character Collections — one-tap photo + voice bundles.
              Killer flow for serialized AI storytelling: same character
              across every clip without re-uploading. Auto-forces Kling. */}
          <div data-testid="character-collections" className="border border-[#B026FF]/30 bg-[#B026FF]/[0.04] p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B026FF]">
                  // characters
                </p>
                <p className="text-[11px] text-white/50 mt-1 leading-tight">
                  Tap a Character to load its photo + voice in one shot. Perfect for serialized content.
                </p>
              </div>
              <div className="shrink-0 flex flex-col gap-2 items-end">
                <button
                  type="button"
                  data-testid="character-new-btn"
                  onClick={openCharacterModal}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-[#B026FF]/60 text-[#B026FF] hover:bg-[#B026FF]/10 text-[10px] font-mono uppercase tracking-wider"
                >
                  <UserPlus className="h-3 w-3" strokeWidth={2} />
                  new character
                </button>
                <button
                  type="button"
                  data-testid="marketplace-open-btn"
                  onClick={openMarketplace}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-[#00FFFF]/60 text-[#00FFFF] hover:bg-[#00FFFF]/10 text-[10px] font-mono uppercase tracking-wider"
                >
                  <Store className="h-3 w-3" strokeWidth={2} />
                  browse marketplace
                </button>
              </div>
            </div>

            {characters.length === 0 ? (
              <p className="text-[11px] text-white/40 italic">
                No Characters yet. Bundle a photo + voice as a reusable identity.
              </p>
            ) : (
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4">
                {characters.map((c, i) => {
                  const selected = selectedCharacterId === c.character_id;
                  const imgSrc = `${API}/ai/characters/${c.character_id}/image`;
                  return (
                    <div
                      key={c.character_id}
                      data-testid={`character-card-${i}`}
                      className={`relative shrink-0 w-32 border-2 transition-all group ${
                        selected
                          ? "border-[#B026FF] shadow-[0_0_18px_rgba(176,38,255,0.5)]"
                          : "border-white/10 hover:border-white/25"
                      }`}
                    >
                      <button
                        type="button"
                        data-testid={`character-select-${i}`}
                        onClick={() => selectCharacter(c)}
                        className="block w-full text-left"
                      >
                        <div className="aspect-square bg-black/60 overflow-hidden relative">
                          <img
                            src={imgSrc}
                            alt={c.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          {selected && (
                            <div className="absolute top-1 left-1 bg-[#B026FF] text-white text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5">
                              active
                            </div>
                          )}
                        </div>
                        <div className="p-2">
                          <p className="text-xs font-bold text-white truncate">{c.name}</p>
                          {c.voice_label ? (
                            <p className="text-[10px] font-mono text-[#00FFFF] mt-0.5 truncate flex items-center gap-1">
                              <Volume2 className="h-2.5 w-2.5 shrink-0" strokeWidth={2} />
                              {c.voice_label}
                            </p>
                          ) : (
                            <p className="text-[10px] font-mono text-white/30 mt-0.5">no voice</p>
                          )}
                        </div>
                      </button>
                      <button
                        type="button"
                        data-testid={`character-delete-${i}`}
                        onClick={(e) => { e.stopPropagation(); deleteCharacter(c.character_id); }}
                        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-black/70 hover:bg-[#FF3B30] p-1 transition-opacity"
                        title="Delete character"
                      >
                        <Trash2 className="h-3 w-3 text-white" strokeWidth={2} />
                      </button>
                      <button
                        type="button"
                        data-testid={`character-publish-${i}`}
                        onClick={(e) => { e.stopPropagation(); togglePublish(c); }}
                        className={`absolute bottom-[62px] right-1 opacity-0 group-hover:opacity-100 bg-black/70 p-1 transition-opacity hover:bg-[#00FFFF] hover:text-black ${
                          c.is_published ? "text-[#00FFFF]" : "text-white/70"
                        }`}
                        title={c.is_published ? "Unpublish from Marketplace" : "Publish to Marketplace"}
                      >
                        <Share2 className="h-3 w-3" strokeWidth={2} />
                      </button>
                      {c.is_published && (
                        <div
                          data-testid={`character-published-badge-${i}`}
                          className="absolute top-1 left-1 flex items-center gap-1 bg-[#00FFFF] text-black text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5"
                          style={{ opacity: selected ? 0 : 1 }}
                        >
                          <Store className="h-2.5 w-2.5" strokeWidth={2.5} />
                          live
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Reference image (optional) — animates uploaded picture */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2 block flex items-center gap-2">
              reference image
              <span className="text-white/30 normal-case tracking-normal font-sans">
                — optional. Sora will animate this frame using your prompt.
              </span>
            </label>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="gen-image-input"
              onChange={(e) => handleImagePick(e.target.files?.[0])}
            />
            {referencePreview ? (
              <div
                data-testid="gen-image-preview"
                className="relative inline-block border-2 border-[#FF007F]/60 group"
                style={{ boxShadow: "0 0 22px rgba(255,0,127,0.35)" }}
              >
                <img
                  src={referencePreview}
                  alt="Reference"
                  className="max-h-48 max-w-full object-contain"
                />
                <button
                  type="button"
                  data-testid="gen-image-clear"
                  onClick={clearReferenceImage}
                  className="absolute top-2 right-2 h-7 w-7 bg-black/80 border border-white/30 hover:border-[#FF007F] hover:text-[#FF007F] text-white flex items-center justify-center transition-colors"
                >
                  <XIcon className="h-3.5 w-3.5" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-black/70 border-t border-[#FF007F]/40">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-[#FF007F]">
                    // will.animate.this
                  </p>
                </div>
              </div>
            ) : (
              <button
                type="button"
                data-testid="gen-image-picker"
                onClick={() => imageInputRef.current?.click()}
                className="w-full border-2 border-dashed border-white/15 hover:border-[#FF007F]/60 hover:bg-[#FF007F]/5 py-8 px-4 flex flex-col items-center gap-2 transition-all group"
              >
                <ImagePlus
                  className="h-8 w-8 text-white/40 group-hover:text-[#FF007F] transition-colors"
                  strokeWidth={1.5}
                />
                <p className="text-white/60 text-sm">
                  drop or click to add a starting image
                </p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/30">
                  jpg · png · webp · gif · max 8mb
                </p>
              </button>
            )}

            {/* Safety tips — collapsible callout on how to phrase prompts that
                pass Sora 2's content-policy filter */}
            <div
              data-testid="gen-safety-tips"
              className="mt-3 border border-[#FFB800]/25 bg-[#FFB800]/[0.03]"
            >
              <button
                type="button"
                onClick={() => setSafetyOpen((v) => !v)}
                data-testid="gen-safety-toggle"
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 hover:bg-[#FFB800]/5 transition-colors text-left"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <ShieldAlert
                    className="h-4 w-4 text-[#FFB800] flex-shrink-0"
                    strokeWidth={2}
                  />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB800]">
                    // safety.tips.for.prompts
                  </span>
                  <span className="text-white/50 text-xs truncate">
                    what Sora accepts vs blocks
                  </span>
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-white/40 flex-shrink-0 transition-transform ${
                    safetyOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {safetyOpen && (
                <div className="px-4 pb-4 pt-1 border-t border-[#FFB800]/15 space-y-3">
                  <p className="text-white/70 text-xs leading-relaxed">
                    Sora 2 has strict guardrails to prevent deepfakes,
                    trademark abuse, and unsafe content. Rejections don&apos;t
                    charge your key — but you save time by phrasing carefully.
                  </p>

                  {/* Blocked list */}
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30] mb-1.5">
                      ✕ usually.blocked
                    </p>
                    <ul className="text-white/70 text-xs space-y-1 pl-3">
                      <li>
                        <span className="text-white">Identifiable real
                          people</span> — celebrities, politicians, someone&apos;s
                        clear face (deepfake protection).
                      </li>
                      <li>
                        <span className="text-white">Named brands / models</span> —
                        e.g. <span className="font-mono text-[#FF3B30]">
                          &quot;Ninja 636&quot;</span>, iPhone,
                        Nike, McDonald&apos;s (trademark).
                      </li>
                      <li>
                        <span className="text-white">Copyrighted characters</span> —
                        e.g. Mickey Mouse, Pikachu, Batman.
                      </li>
                      <li>
                        <span className="text-white">Extreme violence, gore,
                          nudity, or self-harm</span>.
                      </li>
                    </ul>
                  </div>

                  {/* Accepted list */}
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-[#39FF14] mb-1.5">
                      ✓ works.great
                    </p>
                    <ul className="text-white/70 text-xs space-y-1 pl-3">
                      <li>
                        <span className="text-white">Anonymous subjects</span> — 
                        &quot;a rider&quot;, &quot;a woman&quot;, &quot;a
                        skateboarder&quot; · helmet, silhouette, or
                        back-of-head shots pass easier.
                      </li>
                      <li>
                        <span className="text-white">Generic categories</span> —
                        &quot;a green sport motorcycle&quot; instead of
                        &quot;Ninja 636&quot;; &quot;a smartphone&quot;
                        instead of &quot;iPhone&quot;.
                      </li>
                      <li>
                        <span className="text-white">Camera direction &amp; mood</span> —
                        slow-motion, golden hour, drone shot, cinematic
                        lighting, dust trails.
                      </li>
                      <li>
                        <span className="text-white">Stylized worlds</span> —
                        neon-noir, cyberpunk, watercolor, low-poly, anime
                        style. Style shields identity.
                      </li>
                    </ul>
                  </div>

                  {/* Rewrite example */}
                  <div className="border border-white/10 bg-black/40 p-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1.5">
                      // rewrite.example
                    </p>
                    <p className="text-xs text-[#FF3B30] line-through opacity-70">
                      Let this person pop a wheelie on a Ninja 636
                    </p>
                    <p className="text-xs text-[#39FF14] mt-1">
                      → The helmeted rider pops a wheelie on a green sport
                      motorcycle down an empty highway at golden hour, dust
                      trailing behind, cinematic slow-motion.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* prompt */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2 block">
              prompt
            </label>
            <textarea
              data-testid="gen-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={
                referencePreview
                  ? "Describe how this image should move, transform, or animate…"
                  : "Describe the scene, mood, camera movement…"
              }
              rows={4}
              maxLength={500}
              className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-4 py-3 focus:outline-none focus:border-[#00FFFF] focus:ring-1 focus:ring-[#00FFFF]/40 resize-none"
            />
            <p className="font-mono text-[10px] text-white/40 mt-2">
              {prompt.length}/500
            </p>

            <div className="mt-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
                try one of these:
              </p>
              <div className="flex flex-wrap gap-2">
                {(referencePreview ? IMAGE_SAMPLE_PROMPTS : SAMPLE_PROMPTS).map((p, idx) => (
                  <button
                    key={p}
                    type="button"
                    data-testid={`sample-prompt-${idx}`}
                    onClick={() => setPrompt(p)}
                    className="text-left text-xs text-white/60 hover:text-[#00FFFF] border border-white/10 hover:border-[#00FFFF]/40 px-3 py-2 max-w-xs transition-all"
                  >
                    {p.length > 60 ? p.slice(0, 60) + "…" : p}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* AI model picker — Sora 2 (text OR image) vs Kling 3.0 (image + audio) */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2 block">
              ai.model
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="provider-sora"
                onClick={() => { setProvider("sora"); setProviderTouched(true); }}
                className={`border p-4 text-left transition-all ${
                  provider === "sora"
                    ? "border-[#39FF14] bg-[#39FF14]/10 shadow-[0_0_15px_rgba(57,255,20,0.25)]"
                    : "border-white/10 hover:border-white/30"
                }`}
              >
                <p className={`font-display font-bold text-base ${provider === "sora" ? "text-[#39FF14]" : "text-white"}`}>
                  Sora 2
                </p>
                <p className="text-[10px] text-white/50 mt-1 leading-tight">
                  OpenAI · text OR image → video · 4–12s · 2–5 min render
                </p>
              </button>
              <button
                type="button"
                data-testid="provider-kling"
                disabled={!referenceImage}
                onClick={() => {
                  if (!referenceImage) return;
                  setProvider("kling");
                  setProviderTouched(true);
                }}
                title={!referenceImage ? "Kling 3.0 needs a reference image — upload one first" : ""}
                className={`border p-4 text-left transition-all ${
                  provider === "kling"
                    ? "border-[#FF007F] bg-[#FF007F]/10 shadow-[0_0_15px_rgba(255,0,127,0.25)]"
                    : referenceImage
                      ? "border-white/10 hover:border-white/30"
                      : "border-white/5 opacity-40 cursor-not-allowed"
                }`}
              >
                <p className={`font-display font-bold text-base ${provider === "kling" ? "text-[#FF007F]" : "text-white"}`}>
                  Kling 3.0
                </p>
                <p className="text-[10px] text-white/50 mt-1 leading-tight">
                  fal.ai · photo → video WITH audio · 3–15s · 60–180s render
                </p>
                <p className="text-[10px] text-[#FF007F]/70 mt-1 leading-tight font-mono">
                  ~$1.06 / 5s · ~$2.04 / 8s · ~$3.02 / 12s
                </p>
              </button>
            </div>
            {provider === "kling" && (
              <p className="mt-2 text-[10px] font-mono text-[#FF007F]/80">
                // KLING 3.0 renders sharper humans + native audio. Requires a reference photo.
              </p>
            )}
          </div>

          {/* Kling Voice Library — lock a character voice across generations */}
          {provider === "kling" && (
            <div data-testid="voice-library" className="border border-[#FF007F]/30 bg-[#FF007F]/[0.03] p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007F]">
                    // voice.library
                  </p>
                  <p className="text-[11px] text-white/50 mt-1 leading-tight">
                    Clone a voice, then reference it in your prompt as <span className="font-mono text-[#00FFFF]">{"<<<voice_1>>>"}</span> or <span className="font-mono text-[#00FFFF]">{"<<<voice_2>>>"}</span>. Max 2 per generation.
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="voice-add-btn"
                  onClick={() => setVoiceUploadOpen((v) => !v)}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 border border-[#FF007F]/60 text-[#FF007F] hover:bg-[#FF007F]/10 text-[10px] font-mono uppercase tracking-wider"
                >
                  <Mic className="h-3 w-3" strokeWidth={2} />
                  {voiceUploadOpen ? "cancel" : "clone voice"}
                </button>
              </div>

              {voiceUploadOpen && (
                <div data-testid="voice-upload-panel" className="border border-white/10 p-3 mb-3 space-y-3 bg-black/40">
                  <input
                    type="text"
                    data-testid="voice-label-input"
                    placeholder="Voice name (e.g. Narrator Nova)"
                    value={voiceLabel}
                    onChange={(e) => setVoiceLabel(e.target.value.slice(0, 60))}
                    className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#FF007F] focus:outline-none"
                  />
                  <input
                    ref={voiceFileRef}
                    type="file"
                    data-testid="voice-file-input"
                    accept="audio/*,video/mp4,video/quicktime"
                    onChange={(e) => setVoiceFile(e.target.files?.[0] || null)}
                    className="w-full text-[11px] text-white/70 file:mr-3 file:py-1.5 file:px-3 file:border file:border-white/20 file:bg-transparent file:text-white/80 file:font-mono file:text-[10px] file:uppercase file:cursor-pointer hover:file:bg-white/5"
                  />
                  <p className="text-[10px] text-white/40 font-mono">
                    // audio 5–30s · single clean voice · mp3/wav/mp4/mov
                  </p>
                  <button
                    type="button"
                    data-testid="voice-clone-submit"
                    onClick={uploadVoice}
                    disabled={voiceUploading || !voiceFile || !voiceLabel.trim()}
                    className="w-full bg-[#FF007F] text-white font-mono uppercase tracking-wider text-xs py-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#FF007F]/90"
                  >
                    {voiceUploading ? "cloning…" : "clone this voice"}
                  </button>
                </div>
              )}

              {voices.length === 0 ? (
                <p className="text-[11px] text-white/40 italic">
                  No voices cloned yet. Add one to lock a consistent character voice.
                </p>
              ) : (
                <div className="space-y-2">
                  {voices.map((v, i) => {
                    const status = v.status || (v.voice_id ? "ready" : "pending");
                    const rowKey = v.voice_id || v.pending_id || `v-${i}`;
                    const isPending = status === "pending";
                    const isFailed = status === "failed";
                    const isReady = status === "ready" && !!v.voice_id;
                    const idx = isReady ? selectedVoiceIds.indexOf(v.voice_id) : -1;
                    const selected = idx !== -1;
                    return (
                      <div
                        key={rowKey}
                        data-testid={`voice-item-${i}`}
                        className={`flex items-center justify-between border p-2 transition-all ${
                          isFailed
                            ? "border-[#FF3B30]/40 bg-[#FF3B30]/5"
                            : isPending
                              ? "border-white/10 bg-white/[0.02] opacity-70"
                              : selected
                                ? "border-[#00FFFF] bg-[#00FFFF]/10"
                                : "border-white/10 hover:border-white/25"
                        }`}
                      >
                        <button
                          type="button"
                          data-testid={`voice-toggle-${i}`}
                          onClick={() => isReady && toggleVoice(v.voice_id)}
                          disabled={!isReady}
                          className="flex-1 flex items-center gap-2 text-left disabled:cursor-not-allowed"
                        >
                          {isPending ? (
                            <Loader2 className="h-4 w-4 shrink-0 text-[#FFC300] animate-spin" strokeWidth={2} />
                          ) : (
                            <Volume2
                              className={`h-4 w-4 shrink-0 ${isFailed ? "text-[#FF3B30]" : selected ? "text-[#00FFFF]" : "text-white/40"}`}
                              strokeWidth={2}
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-white truncate flex items-center gap-1.5">
                              {v.label}
                              {isPending && (
                                <span className="font-mono text-[9px] uppercase tracking-widest text-[#FFC300] border border-[#FFC300]/50 px-1 py-0.5">
                                  cloning
                                </span>
                              )}
                              {isFailed && (
                                <span className="font-mono text-[9px] uppercase tracking-widest text-[#FF3B30] border border-[#FF3B30]/50 px-1 py-0.5">
                                  failed
                                </span>
                              )}
                            </p>
                            {isFailed && v.error && (
                              <p className="text-[10px] text-[#FF3B30]/80 mt-0.5 truncate" title={v.error}>
                                {v.error}
                              </p>
                            )}
                            {selected && isReady && (
                              <p className="text-[10px] font-mono text-[#00FFFF] mt-0.5">
                                {`bound to <<<voice_${idx + 1}>>>`}
                              </p>
                            )}
                          </div>
                        </button>
                        <button
                          type="button"
                          data-testid={`voice-delete-${i}`}
                          onClick={() => deleteVoice(v.voice_id || v.pending_id)}
                          className="p-1.5 text-white/40 hover:text-[#FF3B30]"
                          title="Delete voice"
                        >
                          <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* duration */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2 block">
              duration
            </label>
            <div className="grid grid-cols-3 gap-3">
              {DURATIONS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  data-testid={`duration-${d.value}`}
                  onClick={() => setDuration(d.value)}
                  className={`border p-4 text-center transition-all ${
                    duration === d.value
                      ? "border-[#39FF14] bg-[#39FF14]/10 shadow-[0_0_15px_rgba(57,255,20,0.25)]"
                      : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <Clock
                    className={`h-5 w-5 mx-auto mb-2 ${
                      duration === d.value ? "text-[#39FF14]" : "text-white/50"
                    }`}
                    strokeWidth={1.5}
                  />
                  <p
                    className={`font-display font-bold text-xl ${
                      duration === d.value ? "text-[#39FF14]" : "text-white"
                    }`}
                  >
                    {d.label}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-1">
                    {d.note}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* optional caption */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2 block">
              caption (optional — defaults to your prompt)
            </label>
            <textarea
              data-testid="gen-caption-input"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Add hashtags, drop a vibe…"
              rows={2}
              className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-4 py-2.5 focus:outline-none focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F]/40 resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={submitting || isActive || !prompt.trim()}
            data-testid="gen-submit-btn"
            className="w-full bg-[#39FF14] text-black font-bold uppercase tracking-widest py-4 hover:shadow-[0_0_25px_rgba(57,255,20,0.6)] disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> starting…
              </>
            ) : isActive ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> generating —
                please wait
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5" /> Generate video
              </>
            )}
          </button>
        </form>

        {/* History */}
        {recent.length > 0 && (
          <section className="mt-14">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-4">
              // your generations
            </p>
            <ul className="space-y-2">
              {recent.map((j) => {
                const s = STATUS_LABELS[j.status] || STATUS_LABELS.pending;
                return (
                  <li
                    key={j.job_id}
                    data-testid={`recent-job-${j.job_id}`}
                    className="border border-white/10 p-4 flex items-start gap-4 hover:border-white/20 transition-colors"
                  >
                    <div
                      className="h-12 w-12 flex items-center justify-center border flex-shrink-0"
                      style={{ borderColor: s.color }}
                    >
                      <Film className="h-5 w-5" style={{ color: s.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className="font-mono text-[10px] uppercase tracking-[0.3em] mb-1"
                        style={{ color: s.color }}
                      >
                        // {s.label}
                      </p>
                      <p className="text-sm text-white truncate">
                        {j.prompt}
                      </p>
                      <p className="font-mono text-[10px] text-white/30 mt-1">
                        {new Date(j.created_at).toLocaleString()} · {j.duration}s
                      </p>
                    </div>
                    {j.status === "done" && j.video_id && (
                      <button
                        onClick={() => navigate(`/v/${j.video_id}`)}
                        data-testid={`recent-watch-${j.job_id}`}
                        className="text-xs font-mono uppercase tracking-widest text-[#39FF14] hover:underline whitespace-nowrap"
                      >
                        watch →
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>

      {/* Character creation modal */}
      {characterModalOpen && (
        <div
          data-testid="character-modal"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setCharacterModalOpen(false)}
        >
          <div
            className="w-full max-w-md bg-[#0A0A0C] border border-[#B026FF]/50 shadow-[0_0_40px_rgba(176,38,255,0.35)] p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B026FF]">
                  // new.character
                </p>
                <h3 className="font-display font-black text-2xl mt-1">Bundle photo + voice</h3>
              </div>
              <button
                type="button"
                data-testid="character-modal-close"
                onClick={() => setCharacterModalOpen(false)}
                className="p-1 text-white/50 hover:text-white"
              >
                <XIcon className="h-5 w-5" strokeWidth={2} />
              </button>
            </div>

            <input
              type="text"
              data-testid="character-name-input"
              placeholder="Character name (e.g. Nova the Narrator)"
              value={charName}
              onChange={(e) => setCharName(e.target.value.slice(0, 60))}
              className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#B026FF] focus:outline-none"
            />

            <textarea
              data-testid="character-description-input"
              placeholder="Short bio (optional) — helps you remember the vibe"
              value={charDescription}
              onChange={(e) => setCharDescription(e.target.value.slice(0, 200))}
              rows={2}
              className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#B026FF] focus:outline-none resize-none"
            />

            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">reference photo</p>
              <input
                ref={charImageRef}
                type="file"
                accept="image/*"
                data-testid="character-image-input"
                className="hidden"
                onChange={(e) => pickCharacterImage(e.target.files?.[0])}
              />
              {charImagePreview ? (
                <div className="relative inline-block border-2 border-[#B026FF]/60">
                  <img src={charImagePreview} alt="preview" className="max-h-40" />
                  <button
                    type="button"
                    data-testid="character-image-clear"
                    onClick={() => { setCharImageFile(null); setCharImagePreview(null); }}
                    className="absolute top-1 right-1 bg-black/70 p-1"
                  >
                    <XIcon className="h-3 w-3 text-white" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="character-image-pick"
                  onClick={() => charImageRef.current?.click()}
                  className="w-full border border-dashed border-white/20 hover:border-[#B026FF] py-6 flex flex-col items-center gap-2 text-white/50 hover:text-[#B026FF] transition-all"
                >
                  <ImagePlus className="h-6 w-6" strokeWidth={1.5} />
                  <span className="text-xs font-mono uppercase tracking-wider">upload photo</span>
                </button>
              )}
            </div>

            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">voice (optional)</p>
              <select
                data-testid="character-voice-select"
                value={charVoiceId}
                onChange={(e) => setCharVoiceId(e.target.value)}
                className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white focus:border-[#B026FF] focus:outline-none"
              >
                <option value="" className="bg-[#0A0A0C]">— no voice —</option>
                {voices.filter((v) => v.status !== "failed" && v.status !== "pending" && v.voice_id).map((v) => (
                  <option key={v.voice_id} value={v.voice_id} className="bg-[#0A0A0C]">{v.label}</option>
                ))}
              </select>
              {voices.length === 0 && (
                <p className="text-[10px] font-mono text-white/40 mt-1">
                  // clone a voice first via the Voice Library
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                data-testid="character-modal-cancel"
                onClick={() => setCharacterModalOpen(false)}
                className="flex-1 border border-white/15 py-2.5 text-xs font-mono uppercase tracking-wider text-white/70 hover:border-white/30"
              >
                cancel
              </button>
              <button
                type="button"
                data-testid="character-modal-save"
                onClick={saveCharacter}
                disabled={charSaving || !charName.trim() || !charImageFile}
                className="flex-1 bg-[#B026FF] text-white py-2.5 text-xs font-mono uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-[0_0_20px_rgba(176,38,255,0.55)]"
              >
                {charSaving ? "saving…" : "save character"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Marketplace modal — public gallery of published Characters */}
      {marketOpen && (
        <div
          data-testid="marketplace-modal"
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4"
          onClick={() => setMarketOpen(false)}
        >
          <div
            className="w-full max-w-4xl bg-[#0A0A0C] border border-[#00FFFF]/40 shadow-[0_0_45px_rgba(0,255,255,0.35)] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FFFF]">
                  // marketplace
                </p>
                <h3 className="font-display font-black text-2xl mt-1">Character Marketplace</h3>
                <p className="text-[11px] text-white/50 mt-1">
                  Remix any Character into your library. Tip the creator to say thanks.
                </p>
              </div>
              <button
                type="button"
                data-testid="marketplace-close"
                onClick={() => setMarketOpen(false)}
                className="p-1 text-white/50 hover:text-white"
              >
                <XIcon className="h-5 w-5" strokeWidth={2} />
              </button>
            </div>

            <div className="flex gap-2 mb-4">
              {[
                { key: "trending", label: "Trending", icon: TrendingUp },
                { key: "new", label: "New", icon: Sparkles },
                { key: "top_tipped", label: "Top Tipped", icon: Coins },
              ].map((s) => (
                <button
                  key={s.key}
                  type="button"
                  data-testid={`market-sort-${s.key}`}
                  onClick={() => changeMarketSort(s.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 border text-[10px] font-mono uppercase tracking-wider transition-all ${
                    marketSort === s.key
                      ? "border-[#00FFFF] bg-[#00FFFF]/10 text-[#00FFFF]"
                      : "border-white/10 text-white/60 hover:border-white/25"
                  }`}
                >
                  <s.icon className="h-3 w-3" strokeWidth={2} />
                  {s.label}
                </button>
              ))}
            </div>

            {marketLoading ? (
              <div className="py-16 flex items-center justify-center">
                <Loader2 className="h-6 w-6 text-[#00FFFF] animate-spin" />
              </div>
            ) : marketItems.length === 0 ? (
              <p className="text-center py-16 text-white/40 italic text-sm">
                No published Characters yet. Be the first — publish yours to grow the gallery.
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto pr-1">
                {marketItems.map((mp, i) => {
                  const imgSrc = `${API}/marketplace/characters/${mp.character_id}/image`;
                  const isOwn = user && mp.creator?.user_id === user.user_id;
                  return (
                    <div
                      key={mp.character_id}
                      data-testid={`market-card-${i}`}
                      className="border border-white/10 bg-black/40 hover:border-[#00FFFF]/40 transition-all flex flex-col"
                    >
                      <div className="aspect-square bg-black/60 overflow-hidden">
                        <img
                          src={imgSrc}
                          alt={mp.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-2 flex-1 flex flex-col">
                        <p className="text-xs font-bold text-white truncate">{mp.name}</p>
                        <p className="text-[10px] text-white/50 truncate">@{mp.creator?.username || "unknown"}</p>
                        <div className="flex items-center gap-2 mt-1 text-[10px] font-mono text-white/40">
                          <span className="flex items-center gap-1">
                            <Repeat2 className="h-3 w-3" strokeWidth={2} />
                            {mp.remix_count}
                          </span>
                          <span className="flex items-center gap-1">
                            <Coins className="h-3 w-3" strokeWidth={2} />
                            ${(mp.tip_total_cents / 100).toFixed(2)}
                          </span>
                          {mp.has_voice && (
                            <span className="flex items-center gap-1 text-[#00FFFF]/70">
                              <Volume2 className="h-3 w-3" strokeWidth={2} />
                            </span>
                          )}
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-1">
                          <button
                            type="button"
                            data-testid={`market-remix-${i}`}
                            onClick={() => remixCharacter(mp)}
                            disabled={isOwn || remixingId === mp.character_id}
                            title={isOwn ? "You can't remix your own character" : ""}
                            className="bg-[#00FFFF] text-black text-[10px] font-mono uppercase tracking-wider py-1.5 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#00FFFF]/90 flex items-center justify-center gap-1"
                          >
                            {remixingId === mp.character_id ? (
                              <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
                            ) : (
                              <Repeat2 className="h-3 w-3" strokeWidth={2} />
                            )}
                            remix
                          </button>
                          <button
                            type="button"
                            data-testid={`market-tip-${i}`}
                            onClick={() => { setTipTargetId(mp.character_id); setTipAmount("100"); }}
                            disabled={isOwn}
                            title={isOwn ? "You can't tip yourself" : ""}
                            className="border border-[#B026FF]/60 text-[#B026FF] text-[10px] font-mono uppercase tracking-wider py-1.5 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#B026FF]/10 flex items-center justify-center gap-1"
                          >
                            <Coins className="h-3 w-3" strokeWidth={2} />
                            tip
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tip modal — amount input for character creator tip */}
      {tipTargetId && (
        <div
          data-testid="tip-modal"
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setTipTargetId(null)}
        >
          <div
            className="w-full max-w-sm bg-[#0A0A0C] border border-[#B026FF]/50 shadow-[0_0_40px_rgba(176,38,255,0.35)] p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B026FF]">
                  // tip.creator
                </p>
                <h3 className="font-display font-black text-xl mt-1">Send a tip</h3>
              </div>
              <button
                type="button"
                data-testid="tip-modal-close"
                onClick={() => setTipTargetId(null)}
                className="p-1 text-white/50 hover:text-white"
              >
                <XIcon className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2 block">
                amount (cents)
              </label>
              <input
                type="number"
                min={25}
                max={10000}
                step={25}
                data-testid="tip-amount-input"
                value={tipAmount}
                onChange={(e) => setTipAmount(e.target.value)}
                className="w-full bg-transparent border border-white/15 px-3 py-2 text-lg text-white focus:border-[#B026FF] focus:outline-none font-mono"
              />
              <p className="text-[10px] font-mono text-white/40 mt-1">
                = ${(parseInt(tipAmount || "0", 10) / 100).toFixed(2)} · min $0.25 · max $100
              </p>
              <div className="flex gap-1 mt-2">
                {[100, 250, 500, 1000].map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`tip-quick-${c}`}
                    onClick={() => setTipAmount(String(c))}
                    className="flex-1 border border-white/10 hover:border-[#B026FF] py-1 text-[10px] font-mono text-white/70 hover:text-[#B026FF]"
                  >
                    ${(c / 100).toFixed(2)}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              data-testid="tip-submit"
              onClick={submitTip}
              disabled={tipSubmitting}
              className="w-full bg-[#B026FF] text-white py-2.5 text-xs font-mono uppercase tracking-wider disabled:opacity-40 hover:shadow-[0_0_20px_rgba(176,38,255,0.55)]"
            >
              {tipSubmitting ? "sending…" : `send tip`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
